/* global grecaptcha */
import React, { useState, useEffect } from "react";

const initialState = {
  name: "",
  email: "",
  message: "",
};

// Enquiries go to the Google Apps Script in contact-backend/, which checks the
// reCAPTCHA with Google before saving to the sheet. Until that script is
// deployed and REACT_APP_CONTACT_ENDPOINT is set, they go to SheetDB as before.
// Both reply with { created: 1 } on success.
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT ||
  "https://sheetdb.io/api/v1/phhh0f4sh5nrf";

const Contact = ({ data }) => {
  const [formData, setFormData] = useState(initialState);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);

  useEffect(() => {
    const loadRecaptcha = () => {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    };

    loadRecaptcha();
    window.verifyRecaptchaCallback = (token) => {
      if (token) {
        setRecaptchaVerified(true);
      }
    };
    window.expiredRecaptchaCallback = () => {
      setRecaptchaVerified(false);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = grecaptcha.getResponse();
    if (!token) {
      setRecaptchaVerified(false);
      setStatus({ type: "error", text: "Please tick the reCAPTCHA box and try again." });
      return;
    }

    const body = new URLSearchParams({
      "data[name]": formData.name,
      "data[email]": formData.email,
      "data[query]": formData.message,
      "data[created_date]": new Date().toISOString(),
      "data[recaptcha_token]": token,
    });

    setSending(true);
    try {
      const response = await fetch(CONTACT_ENDPOINT, { method: "POST", body });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.created) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }
      setStatus({ type: "success", text: "Your message was successfully sent!" });
      setFormData({ ...initialState });
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus({
        type: "error",
        text: "Sorry, your message could not be sent. Please try again, or email us directly.",
      });
    } finally {
      setSending(false);
      grecaptcha.reset();
      setRecaptchaVerified(false);
    }
  };

  return (
    <div>
      <div id="contact">
        <div className="container">
          <div className="col-md-8">
            <div className="row">
              
              <div className="section-title">

                <h2>Get In Touch</h2>
                <p>
                  Please fill out the form below to send us an email and we will
                  get back to you as soon as possible.
                </p>
              </div>
              <form name="contactForm" validate="true" onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder="Name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  
                </div>
                <div className="form-group">
                  <textarea
                    name="message"
                    id="message"
                    className="form-control"
                    rows="4"
                    placeholder="Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                <div
                  className="g-recaptcha"
                  data-sitekey="6LdjpQIqAAAAADX0TgfWh0XcB1A3s4kPW1H5vq_v"
                  data-callback="verifyRecaptchaCallback"
                  data-expired-callback="expiredRecaptchaCallback"
                  style={{ marginBottom: '10px' }}
                ></div>
                <div id="success"></div>
                <button type="submit" className="btn btn-custom btn-lg" disabled={!recaptchaVerified || sending}>
                  {sending ? "Sending..." : "Send Message"}
                </button>
                {status && (
                  <div
                    className={`alert ${status.type === "success" ? "alert-success" : "alert-danger"}`}
                    style={{ marginTop: '10px' }}
                  >
                    {status.text}
                  </div>
                )}
              </form>
            </div>
          </div>
          <div className="col-md-3 col-md-offset-1 contact-info">
            <div className="contact-item">
              <h3>Contact Info</h3>
              <p>
                <span>
                  <i className="fa fa-map-marker"></i> Address
                </span>{" "}
                {data ? data.address : "Loading..."} <br/>
                {data ? data.address1 : "Loading..."}<br/>
                {data ? data.address2 : "Loading..."}<br/>
                {data ? data.address3 : "Loading..."}
              </p>
            </div>
            <div className="contact-item">
              <p>
                <span>
                  <i className="fa fa-phone"></i> Landline
                </span>{" "}
                {data ? data.landline : "Loading..."}
              </p>
            </div>
            <div className="contact-item">
              <p>
                <span>
                  <i className="fa fa-fax"></i> Fax
                </span>{" "}
                {data ? data.fax : "Loading..."}
              </p>
            </div>
            <div className="contact-item">
              <p>
                <span>
                  <i className="fa fa-envelope-o"></i> Email
                </span>{" "}
                {data ? data.email : "Loading..."}
              </p>
            </div>
            <div className="contact-item">
              <p>
                <span>
                  <i className="fa fa-briefcase"></i> GST No.
                </span>{" "}
                {data ? data.gstno : "Loading..."}
              </p>
            </div>
          </div>
          <div className="col-md-12" style={{marginBottom:'30px'}}>
            <div className="row">
              <div className="social">
                <ul>
                  <li>
                    <a target="_blank" rel="noopener noreferrer" href={data ? data.facebook : "/"}>
                      <i className="fa fa-facebook"></i>
                    </a>
                  </li>
                  <li>
                    <a target="_blank" rel="noopener noreferrer" href={data ? data.linkedin : "/"}>
                      <i className="fa fa-linkedin-square"></i>
                    </a>
                  </li>
                 
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
